import React from 'react';
import { cn } from '@/lib/utils';
import { Event } from '@/lib/types';
import { eventTypeLabels } from '@/lib/eventLabels';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Calendar, MapPin, Clock, Award } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { hu } from 'date-fns/locale';

interface CalendarEventProps {
  event: Event;
  onClick: (event: Event) => void;
  cellWidth: number;
  isMultiDay?: boolean;
  startDayIndex?: number;
  endDayIndex?: number;
  verticalPosition?: number;
  style?: React.CSSProperties;
}

const CalendarEvent = ({
  event,
  onClick,
  cellWidth,
  isMultiDay = false,
  startDayIndex = 0,
  endDayIndex = 0,
  verticalPosition = 0,
  style = {},
}: CalendarEventProps) => {
  const getBackgroundColor = () => {
    const types = {
      'conference': 'bg-purple-100 hover:bg-purple-200 border-purple-300',
      'webinar': 'bg-blue-100 hover:bg-blue-200 border-blue-300',
      'workshop': 'bg-green-100 hover:bg-green-200 border-green-300',
      'training': 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300',
      'meetup': 'bg-pink-100 hover:bg-pink-200 border-pink-300',
      'seminar': 'bg-orange-100 hover:bg-orange-200 border-orange-300',
      'networking': 'bg-indigo-100 hover:bg-indigo-200 border-indigo-300',
      'exhibition': 'bg-teal-100 hover:bg-teal-200 border-teal-300'
    };

    return types[event.type as keyof typeof types] || 'bg-gray-100 hover:bg-gray-200 border-gray-300';
  };

  function isSameDay(date1: Date, date2: Date) {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  const formatEventDate = () => {
    const startDate = parseISO(event.dateStart);
    const endDate = parseISO(event.dateEnd);
    
    if (isToday(startDate)) {
      return 'Ma';
    }
    
    if (isTomorrow(startDate)) {
      return 'Holnap';
    }
    
    return `${format(startDate, 'MMM d, yyyy', { locale: hu })}${!isSameDay(startDate, endDate) ? ` - ${format(endDate, 'MMM d, yyyy', { locale: hu })}` : ''}`;
  };

  const formatEventTime = () => {
    const startDate = parseISO(event.dateStart);
    const endDate = parseISO(event.dateEnd);
    return `${format(startDate, 'HH:mm', { locale: hu })} - ${format(endDate, 'HH:mm', { locale: hu })}`;
  };

  const baseStyle: React.CSSProperties = {
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  };

  const combinedStyle: React.CSSProperties = {
    ...baseStyle,
    ...style
  };

  const eventContent = (
    <div
      className={cn(
        'border-l-2 py-1 px-2 rounded-sm text-left overflow-hidden shadow-sm',
        getBackgroundColor(),
      )}
      style={combinedStyle}
      onClick={() => onClick(event)}
      onMouseEnter={(e) => {
        const target = e.currentTarget;
        target.style.transform = 'scale(1.02)';
        target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        target.style.zIndex = '30';
      }}
      onMouseLeave={(e) => {
        const target = e.currentTarget;
        target.style.transform = '';
        target.style.boxShadow = '';
        target.style.zIndex = isMultiDay ? '10' : '1';
      }}
    >
      <div className="flex items-start gap-1 flex-wrap h-full w-full">
        <div className={cn("text-xs font-medium truncate w-full", isMultiDay ? "text-gray-800" : "text-gray-700")}>
          {event.name}
        </div>
        {!isMultiDay && (
          <div className="text-xs text-gray-500 truncate">
            {eventTypeLabels[event.type as keyof typeof eventTypeLabels] || event.type}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {eventContent}
        </TooltipTrigger>
        <TooltipContent
          className="p-0 overflow-hidden rounded-md"
          sideOffset={5}
        >
          <div className="max-w-xs bg-white shadow-lg rounded-md overflow-hidden">
            <div className={cn("p-3 text-white",
              event.type === 'conference' ? "bg-purple-500" :
                event.type === 'webinar' ? "bg-blue-500" :
                  event.type === 'workshop' ? "bg-green-500" :
                    event.type === 'training' ? "bg-yellow-500" :
                      event.type === 'meetup' ? "bg-pink-500" :
                        event.type === 'seminar' ? "bg-orange-500" :
                          event.type === 'networking' ? "bg-indigo-500" :
                            event.type === 'exhibition' ? "bg-teal-500" : "bg-gray-500"
            )}>
              <h3 className="font-medium">{event.name}</h3>
              <p className="text-xs opacity-90 mt-1">{eventTypeLabels[event.type as keyof typeof eventTypeLabels] || event.type}</p>
            </div>
            <div className="p-3 space-y-2">
              {event.creditPoints !== undefined && event.creditPoints > 0 && (
                <div className="flex items-center text-sm gap-2">
                  <Award className="h-4 w-4 text-blue-500" />
                  <span className="text-gray-700 font-medium">
                    {event.creditPoints} kreditpont
                  </span>
                </div>
              )}
              <div className="flex items-center text-sm gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{formatEventDate()}</span>
              </div>
              <div className="flex items-center text-sm gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{formatEventTime()}</span>
              </div>
              {event.venue && !event.venue.online && (
                <div className="flex items-center text-sm gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700">
                    {event.venue.name}, {event.venue.city}
                  </span>
                </div>
              )}
              {event.venue && event.venue.online && (
                <div className="text-sm text-green-600 font-medium">
                  Online Event
                </div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CalendarEvent;
